import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Seo } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, Stagger, itemVariant } from "../components/Reveal";
import { TPU_APPS, TPU_APP_NAV, IMAGES } from "../data/content";

export default function TpuApplication() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const data = TPU_APPS[slug];
  if (!data) return <Navigate to="/tpu" replace />;
  const c = lang === "de" ? data.de : data.en;
  const img = IMAGES[data.img] || IMAGES.macro;

  return (
    <>
      <Seo
        title={`${c.title} | NORDFLEX® TPU | PlastoNord`}
        description={c.lead}
        path={`/tpu/${slug}`}
        image={img}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "TPU", path: "/tpu" }, { name: data.keyword, path: `/tpu/${slug}` }]}
        schema={{ "@context": "https://schema.org", "@type": "Article", headline: c.title, description: c.lead, about: "Thermoplastic Polyurethane (TPU)" }}
      />
      <section className="border-b border-[#e4e9f0] bg-[#f6f8fb] grain">
        <div className="pn-container py-14 md:py-20">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "TPU", path: "/tpu" }, { name: data.keyword, path: `/tpu/${slug}` }]} />
          <Reveal>
            <p className="overline">{data.keyword}</p>
            <h1 className="mt-4 max-w-4xl font-head text-4xl font-light leading-[1.08] tracking-tight text-[#16181D] md:text-5xl">{c.title}</h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#4a4d52]">{c.lead}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/kontakt" className="btn-primary" data-testid="app-request">{t("cta.request")} <ArrowRight size={16} /></Link>
              <Link to="/tpu" className="btn-ghost">TPU {lang === "de" ? "Übersicht" : "overview"}</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20">
        <div className="pn-container grid items-start gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <Stagger className="space-y-8">
            {c.sections.map(([title, body], i) => (
              <motion.div key={i} variants={itemVariant} className="border-l-2 border-[#E2611C] pl-6">
                <h2 className="font-head text-xl font-semibold text-[#16181D]">{title}</h2>
                <p className="mt-2 leading-relaxed text-[#4a4d52]">{body}</p>
              </motion.div>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <img src={img} alt={`${c.title} – PlastoNord NORDFLEX TPU`} className="rounded-2xl border border-[#e4e9f0] shadow-xl" loading="lazy" />
          </Reveal>
        </div>
      </section>

      {/* internal links to other TPU applications */}
      <section className="bg-[#f6f8fb] py-16">
        <div className="pn-container">
          <h2 className="font-head text-2xl font-medium text-[#16181D]">{lang === "de" ? "Weitere TPU-Anwendungen" : "More TPU applications"}</h2>
          <Stagger className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TPU_APP_NAV.filter(([p]) => p !== `/tpu/${slug}`).map(([path, label]) => (
              <motion.div key={path} variants={itemVariant}>
                <Link to={path} className="pn-card flex items-center justify-between p-5">
                  <span className="font-head font-medium text-[#16181D]">{label[lang]}</span>
                  <ArrowRight size={16} className="text-[#E2611C]" />
                </Link>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>
    </>
  );
}
